import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Switch, Card, Space, Button, Tabs, Select, message, Collapse, Modal, Divider, Spin } from 'antd';
import { SaveOutlined, ReloadOutlined, EyeOutlined, CodeOutlined } from '@ant-design/icons';
import MonacoEditor from '@monaco-editor/react';
import { PDFViewer } from '@react-pdf/renderer';
import { useTheme } from '@/components/common/ThemeProvider';
import { DEFAULT_STAMP_CONFIG, DEFAULT_TEXT_CONTENT, DeductionFormConfig } from '@/modules/loans/components/DeductionFormConfig';
import { DEFAULT_LOAN_APPLICATION_FORM_CONFIG, DEFAULT_LOAN_APPLICATION_TEXT_CONTENT, HAZINA_LOAN_APPLICATION_FORM_CONFIG, LoanApplicationTemplateConfig, stringifyLoanApplicationFormConfig } from '@/modules/loans/components/LoanApplicationFormConfig';
import WebDeductionFormComponent from '@/modules/loans/components/WebDeductionFormComponent';
import SmartLoanApplicationDocument from '@/modules/loans/components/SmartLoanApplicationDocument';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useCompany } from '@/hooks/company/use-company';
import { useProductPropertiesById } from '../hooks/useProductProperties';
import { useGenerateLoanScheduleFromConfig } from '@/shared/api/client/loan-schedule';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { TextArea } = Input;

interface FormTemplateConfigPanelProps {
    glNo: string;
    initialConfig?: string;
    logoUrl?: string;
    watermarkUrl?: string;
    editorMode?: 'all' | 'application' | 'deduction';
    onSave?: (config: string, logoUrl?: string, watermarkUrl?: string) => void;
}

const FormTemplateConfigPanel: React.FC<FormTemplateConfigPanelProps> = ({
    glNo,
    initialConfig,
    logoUrl,
    watermarkUrl,
    editorMode = 'all',
    onSave
}) => {
    const { dark } = useTheme();
    const authUser = useAuthUser();
    const { data: companyData } = useCompany();
    const { data: productPropertiesResponse } = useProductPropertiesById(glNo);
    const [form] = Form.useForm();
    const lastParsedRef = useRef<any>(null);
    const [configJson, setConfigJson] = useState<string>(initialConfig || getDefaultConfig());
    const [activeTab, setActiveTab] = useState<string>('visual');
    const [isValidJson, setIsValidJson] = useState<boolean>(true);
    const [deductionPreviewOpen, setDeductionPreviewOpen] = useState<boolean>(false);
    const [applicationPreviewOpen, setApplicationPreviewOpen] = useState<boolean>(false);
    const [previewConfig, setPreviewConfig] = useState<any>(null);
    const [applicationPreviewConfig, setApplicationPreviewConfig] = useState<any>(null);
    const applicationTextDefaults = DEFAULT_LOAN_APPLICATION_TEXT_CONTENT;
    const productProperties = productPropertiesResponse as any;
    const previewAmount = Number(productProperties?.defaultAmount) || 1000000;
    const previewPeriod = productProperties?.defaultTerm ?? 12;
    const previewProduct = productProperties || { productName: glNo, glNo };
    const { data: previewScheduleResponse, isLoading: isPreviewScheduleLoading } = useGenerateLoanScheduleFromConfig(
        { glNo, amount: previewAmount, period: previewPeriod },
        { query: { enabled: deductionPreviewOpen || applicationPreviewOpen } }
    );
    const previewScheduleData = previewScheduleResponse;

    const showApplicationEditor = editorMode !== 'deduction';
    const showDeductionEditor = editorMode !== 'application';
    const cardTitle = editorMode === 'application'
        ? 'Loan Application Form Configuration'
        : editorMode === 'deduction'
            ? 'Loan Deduction Form Configuration'
            : 'Loan Form Configuration';

    const buildDeductionFormConfig = (parsed: any, values: any): DeductionFormConfig => ({
        header: {
            companyName: values.companyName ?? parsed.header?.companyName,
            companyAddress: values.companyAddress ?? parsed.header?.companyAddress,
            companyPhone: values.companyPhone ?? parsed.header?.companyPhone,
            companyEmail: values.companyEmail ?? parsed.header?.companyEmail,
        },
        branding: {
            logoUrl: values.deductionLogoUrl || parsed.deductionForm?.branding?.logoUrl,
            watermarkUrl: values.deductionWatermarkUrl || parsed.deductionForm?.branding?.watermarkUrl,
            showWatermark: values.deductionShowWatermark ?? parsed.deductionForm?.branding?.showWatermark ?? false,
        },
        stamp: {
            shape: values.stampShape || DEFAULT_STAMP_CONFIG.shape,
            color: values.stampColor || DEFAULT_STAMP_CONFIG.color,
            orgName: values.stampOrgName || DEFAULT_STAMP_CONFIG.orgName,
            topText: values.stampTopText || DEFAULT_STAMP_CONFIG.topText,
            bottomText: values.stampBottomText || DEFAULT_STAMP_CONFIG.bottomText,
            centerLine1: values.stampCenterLine1 || DEFAULT_STAMP_CONFIG.centerLine1,
            centerLine2: values.stampCenterLine2 || DEFAULT_STAMP_CONFIG.centerLine2,
            stampImageUrl: values.stampImageUrl || parsed.deductionForm?.stamp?.stampImageUrl,
        },
        textContent: {
            referencePrefix: values.deductionReferencePrefix || DEFAULT_TEXT_CONTENT.referencePrefix,
            formTitle: values.deductionFormTitle || DEFAULT_TEXT_CONTENT.formTitle,
            subjectLine: values.deductionSubjectLine || DEFAULT_TEXT_CONTENT.subjectLine,
            bodyText: values.deductionBodyText || DEFAULT_TEXT_CONTENT.bodyText,
            consentTitle: values.deductionConsentTitle || DEFAULT_TEXT_CONTENT.consentTitle,
            consentItems: values.deductionConsentItems
                ? values.deductionConsentItems.split('\n').map((s: string) => s.trim()).filter(Boolean)
                : DEFAULT_TEXT_CONTENT.consentItems,
            footerNote: values.deductionFooterNote || DEFAULT_TEXT_CONTENT.footerNote,
            authorizationText: values.deductionAuthorizationText || DEFAULT_TEXT_CONTENT.authorizationText,
            senderThankYouText: values.deductionSenderThankYouText || DEFAULT_TEXT_CONTENT.senderThankYouText,
            signerTitle1: values.deductionSignerTitle1 || DEFAULT_TEXT_CONTENT.signerTitle1,
            signerTitle2: values.deductionSignerTitle2 || DEFAULT_TEXT_CONTENT.signerTitle2,
            receiverHeaderText: values.deductionReceiverHeaderText || DEFAULT_TEXT_CONTENT.receiverHeaderText,
            tableSectionTitle: values.deductionTableSectionTitle || DEFAULT_TEXT_CONTENT.tableSectionTitle,
            consentIntroText: values.deductionConsentIntroText || DEFAULT_TEXT_CONTENT.consentIntroText,
            memberSignatureLabel: values.deductionMemberSignatureLabel || DEFAULT_TEXT_CONTENT.memberSignatureLabel,
            dateLabelInConsent: values.deductionDateLabelInConsent || DEFAULT_TEXT_CONTENT.dateLabelInConsent,
        },
    });

    const buildApplicationTextContent = (values: any) => ({
        referencePrefix: values.applicationReferencePrefix || applicationTextDefaults.referencePrefix,
        applicationReferenceLabel: values.applicationReferenceLabel || applicationTextDefaults.applicationReferenceLabel,
        applicationDateLabel: values.applicationDateLabel || applicationTextDefaults.applicationDateLabel,
        headerSubtitle: values.applicationHeaderSubtitle || applicationTextDefaults.headerSubtitle,
        personalSectionTitle: values.applicationPersonalSectionTitle || applicationTextDefaults.personalSectionTitle,
        loanSectionTitle: values.applicationLoanSectionTitle || applicationTextDefaults.loanSectionTitle,
        purposeSectionTitle: values.applicationPurposeSectionTitle || applicationTextDefaults.purposeSectionTitle,
        purposePrompt: values.applicationPurposePrompt || applicationTextDefaults.purposePrompt,
        collateralSectionTitle: values.applicationCollateralSectionTitle || applicationTextDefaults.collateralSectionTitle,
        collateralSummary: values.applicationCollateralSummary || applicationTextDefaults.collateralSummary,
        collateralDescription: values.applicationCollateralDescription || applicationTextDefaults.collateralDescription,
        collateralCommitment: values.applicationCollateralCommitment || applicationTextDefaults.collateralCommitment,
        penaltySectionTitle: values.applicationPenaltySectionTitle || applicationTextDefaults.penaltySectionTitle,
        penaltyText: values.applicationPenaltyText || applicationTextDefaults.penaltyText,
        accountSectionTitle: values.applicationAccountSectionTitle || applicationTextDefaults.accountSectionTitle,
        accountPrompt: values.applicationAccountPrompt || applicationTextDefaults.accountPrompt,
        insuranceSectionTitle: values.applicationInsuranceSectionTitle || applicationTextDefaults.insuranceSectionTitle,
        insuranceText: values.applicationInsuranceText || applicationTextDefaults.insuranceText,
        officeUseSectionTitle: values.applicationOfficeUseSectionTitle || applicationTextDefaults.officeUseSectionTitle,
        officeUseApprovedDateLabel: values.applicationOfficeUseApprovedDateLabel || applicationTextDefaults.officeUseApprovedDateLabel,
        officeUseApprovedAmountLabel: values.applicationOfficeUseApprovedAmountLabel || applicationTextDefaults.officeUseApprovedAmountLabel,
        officeUseApprovedPeriodLabel: values.applicationOfficeUseApprovedPeriodLabel || applicationTextDefaults.officeUseApprovedPeriodLabel,
        officeUseInterestLabel: values.applicationOfficeUseInterestLabel || applicationTextDefaults.officeUseInterestLabel,
        officeUseApprovalListTitle: values.applicationOfficeUseApprovalListTitle || applicationTextDefaults.officeUseApprovalListTitle,
        approvalTableNameLabel: values.applicationApprovalTableNameLabel || applicationTextDefaults.approvalTableNameLabel,
        approvalTableSignatureLabel: values.applicationApprovalTableSignatureLabel || applicationTextDefaults.approvalTableSignatureLabel,
        approvalTableDateLabel: values.applicationApprovalTableDateLabel || applicationTextDefaults.approvalTableDateLabel,
        applicantSignatureLabel: values.applicationApplicantSignatureLabel || applicationTextDefaults.applicantSignatureLabel,
        signatureDateLabel: values.applicationSignatureDateLabel || applicationTextDefaults.signatureDateLabel,
        monthlyInstallmentLabel: values.applicationMonthlyInstallmentLabel || applicationTextDefaults.monthlyInstallmentLabel,
        disbursementDateLabel: values.applicationDisbursementDateLabel || applicationTextDefaults.disbursementDateLabel,
        maturityDateLabel: values.applicationMaturityDateLabel || applicationTextDefaults.maturityDateLabel,
        interestRateLabel: values.applicationInterestRateLabel || applicationTextDefaults.interestRateLabel,
        totalPrincipalLabel: values.applicationTotalPrincipalLabel || applicationTextDefaults.totalPrincipalLabel,
        totalInterestLabel: values.applicationTotalInterestLabel || applicationTextDefaults.totalInterestLabel,
        totalRepaymentLabel: values.applicationTotalRepaymentLabel || applicationTextDefaults.totalRepaymentLabel,
        applicantDeclarationTitle: values.applicationDeclarationTitle || applicationTextDefaults.applicantDeclarationTitle,
        applicantDeclarationText: values.applicationDeclarationText || applicationTextDefaults.applicantDeclarationText,
        footerPrimary: values.applicationFooterPrimary || applicationTextDefaults.footerPrimary,
        footerSecondary: values.applicationFooterSecondary || applicationTextDefaults.footerSecondary,
    });

    const parseAndSetFields = (parsed: any, overrideLogo?: string, overrideWatermark?: string) => {
        lastParsedRef.current = parsed;
        const consentItems = parsed.deductionForm?.textContent?.consentItems;
        const applicationText = parsed.applicationForm?.textContent || {};
        form.setFieldsValue({
            language: parsed.language,
            logoUrl: overrideLogo ?? parsed.branding?.logoUrl ?? logoUrl,
            watermarkUrl: overrideWatermark ?? parsed.branding?.watermarkUrl ?? watermarkUrl,
            includeFeeBreakdown: parsed.displayOptions?.includeFeeBreakdown,
            includeRepaymentSchedule: parsed.displayOptions?.includeRepaymentSchedule,
            includeGuarantorInfo: parsed.displayOptions?.includeGuarantorInfo,
            includeSignatures: parsed.displayOptions?.includeSignatures,
            showLogo: parsed.header?.showLogo,
            showWatermark: parsed.branding?.showWatermark,
            companyName: parsed.header?.companyName,
            companyAddress: parsed.header?.companyAddress,
            companyPhone: parsed.header?.companyPhone,
            companyEmail: parsed.header?.companyEmail,
            formTitle: parsed.header?.formTitle,
            stampShape: parsed.deductionForm?.stamp?.shape,
            stampColor: parsed.deductionForm?.stamp?.color,
            stampOrgName: parsed.deductionForm?.stamp?.orgName,
            stampTopText: parsed.deductionForm?.stamp?.topText,
            stampBottomText: parsed.deductionForm?.stamp?.bottomText,
            stampCenterLine1: parsed.deductionForm?.stamp?.centerLine1,
            stampCenterLine2: parsed.deductionForm?.stamp?.centerLine2,
            stampImageUrl: parsed.deductionForm?.stamp?.stampImageUrl,
            deductionLogoUrl: parsed.deductionForm?.branding?.logoUrl,
            deductionWatermarkUrl: parsed.deductionForm?.branding?.watermarkUrl,
            deductionShowWatermark: parsed.deductionForm?.branding?.showWatermark,
            deductionReferencePrefix: parsed.deductionForm?.textContent?.referencePrefix,
            deductionFormTitle: parsed.deductionForm?.textContent?.formTitle,
            deductionSubjectLine: parsed.deductionForm?.textContent?.subjectLine,
            deductionBodyText: parsed.deductionForm?.textContent?.bodyText,
            deductionConsentTitle: parsed.deductionForm?.textContent?.consentTitle,
            deductionConsentItems: Array.isArray(consentItems) ? consentItems.join('\n') : consentItems,
            deductionFooterNote: parsed.deductionForm?.textContent?.footerNote,
            deductionAuthorizationText: parsed.deductionForm?.textContent?.authorizationText,
            deductionSenderThankYouText: parsed.deductionForm?.textContent?.senderThankYouText,
            deductionSignerTitle1: parsed.deductionForm?.textContent?.signerTitle1,
            deductionSignerTitle2: parsed.deductionForm?.textContent?.signerTitle2,
            deductionReceiverHeaderText: parsed.deductionForm?.textContent?.receiverHeaderText,
            deductionTableSectionTitle: parsed.deductionForm?.textContent?.tableSectionTitle,
            deductionConsentIntroText: parsed.deductionForm?.textContent?.consentIntroText,
            deductionMemberSignatureLabel: parsed.deductionForm?.textContent?.memberSignatureLabel,
            deductionDateLabelInConsent: parsed.deductionForm?.textContent?.dateLabelInConsent,
            applicationReferencePrefix: applicationText.referencePrefix,
            applicationReferenceLabel: applicationText.applicationReferenceLabel,
            applicationDateLabel: applicationText.applicationDateLabel,
            applicationHeaderSubtitle: applicationText.headerSubtitle,
            applicationPersonalSectionTitle: applicationText.personalSectionTitle,
            applicationLoanSectionTitle: applicationText.loanSectionTitle,
            applicationMonthlyInstallmentLabel: applicationText.monthlyInstallmentLabel,
            applicationDisbursementDateLabel: applicationText.disbursementDateLabel,
            applicationMaturityDateLabel: applicationText.maturityDateLabel,
            applicationInterestRateLabel: applicationText.interestRateLabel,
            applicationTotalPrincipalLabel: applicationText.totalPrincipalLabel,
            applicationTotalInterestLabel: applicationText.totalInterestLabel,
            applicationTotalRepaymentLabel: applicationText.totalRepaymentLabel,
            applicationPurposeSectionTitle: applicationText.purposeSectionTitle,
            applicationPurposePrompt: applicationText.purposePrompt,
            applicationCollateralSectionTitle: applicationText.collateralSectionTitle,
            applicationCollateralSummary: applicationText.collateralSummary,
            applicationCollateralDescription: applicationText.collateralDescription,
            applicationCollateralCommitment: applicationText.collateralCommitment,
            applicationPenaltySectionTitle: applicationText.penaltySectionTitle,
            applicationPenaltyText: applicationText.penaltyText,
            applicationAccountSectionTitle: applicationText.accountSectionTitle,
            applicationAccountPrompt: applicationText.accountPrompt,
            applicationInsuranceSectionTitle: applicationText.insuranceSectionTitle,
            applicationInsuranceText: applicationText.insuranceText,
            applicationOfficeUseSectionTitle: applicationText.officeUseSectionTitle,
            applicationOfficeUseApprovalListTitle: applicationText.officeUseApprovalListTitle,
            applicationDeclarationTitle: applicationText.applicantDeclarationTitle,
            applicationDeclarationText: applicationText.applicantDeclarationText,
            applicationFooterPrimary: applicationText.footerPrimary,
            applicationFooterSecondary: applicationText.footerSecondary,
            applicationApprovalRows: parsed.applicationForm?.display?.approvalRows,
            applicationShowPenaltySection: parsed.applicationForm?.display?.showPenaltySection,
            applicationShowInsuranceSection: parsed.applicationForm?.display?.showInsuranceSection,
            applicationShowInterestRate: parsed.applicationForm?.display?.showInterestRate,
            applicationShowDisbursementDate: parsed.applicationForm?.display?.showDisbursementDate,
            applicationShowMaturityDate: parsed.applicationForm?.display?.showMaturityDate,
            applicationShowTotalPrincipal: parsed.applicationForm?.display?.showTotalPrincipal,
            applicationShowTotalInterest: parsed.applicationForm?.display?.showTotalInterest,
            applicationShowTotalRepayment: parsed.applicationForm?.display?.showTotalRepayment,
        });
    };

    useEffect(() => {
        const nextConfig = initialConfig || getDefaultConfig();
        setConfigJson(nextConfig);
        try {
            const parsed = JSON.parse(nextConfig);
            parseAndSetFields(parsed, logoUrl, watermarkUrl);
        } catch (e) {
            console.error('Error parsing config JSON:', e);
        }
    }, [initialConfig, logoUrl, watermarkUrl, form]);

    function getDefaultConfig(): string {
        return stringifyLoanApplicationFormConfig(DEFAULT_LOAN_APPLICATION_FORM_CONFIG);
    }

    const handleVisualFormChange = () => {
        const values = form.getFieldsValue();
        const last = lastParsedRef.current || {};
        const consentItemsRaw: string = values.deductionConsentItems || '';
        const consentItems = consentItemsRaw.split('\n').map((s: string) => s.trim()).filter(Boolean);
        const config = {
            language: values.language || last.language || 'EN',
            header: {
                companyName: values.companyName,
                companyAddress: values.companyAddress,
                companyPhone: values.companyPhone,
                companyEmail: values.companyEmail,
                formTitle: values.formTitle,
                showLogo: values.showLogo ?? (last.header?.showLogo ?? true),
            },
            displayOptions: showApplicationEditor ? {
                includeFeeBreakdown: values.includeFeeBreakdown ?? true,
                includeRepaymentSchedule: values.includeRepaymentSchedule ?? true,
                includeGuarantorInfo: values.includeGuarantorInfo ?? true,
                includeSignatures: values.includeSignatures ?? true
            } : last.displayOptions,
            branding: showApplicationEditor ? {
                logoUrl: values.logoUrl,
                watermarkUrl: values.watermarkUrl,
                showWatermark: values.showWatermark ?? false
            } : last.branding,
            applicationForm: showApplicationEditor ? {
                display: {
                    approvalRows: Number(values.applicationApprovalRows || DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.approvalRows || 3),
                    showPenaltySection: values.applicationShowPenaltySection ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showPenaltySection ?? true,
                    showInsuranceSection: values.applicationShowInsuranceSection ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showInsuranceSection ?? true,
                    showInterestRate: values.applicationShowInterestRate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showInterestRate ?? true,
                    showDisbursementDate: values.applicationShowDisbursementDate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showDisbursementDate ?? true,
                    showMaturityDate: values.applicationShowMaturityDate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showMaturityDate ?? true,
                    showTotalPrincipal: values.applicationShowTotalPrincipal ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalPrincipal ?? true,
                    showTotalInterest: values.applicationShowTotalInterest ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalInterest ?? true,
                    showTotalRepayment: values.applicationShowTotalRepayment ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalRepayment ?? true,
                },
                textContent: buildApplicationTextContent(values),
            } : last.applicationForm,
            deductionForm: showDeductionEditor ? {
                branding: {
                    logoUrl: values.deductionLogoUrl,
                    watermarkUrl: values.deductionWatermarkUrl,
                    showWatermark: values.deductionShowWatermark ?? false,
                },
                stamp: {
                    shape: values.stampShape || DEFAULT_STAMP_CONFIG.shape,
                    color: values.stampColor || DEFAULT_STAMP_CONFIG.color,
                    orgName: values.stampOrgName || DEFAULT_STAMP_CONFIG.orgName,
                    topText: values.stampTopText || DEFAULT_STAMP_CONFIG.topText,
                    bottomText: values.stampBottomText || DEFAULT_STAMP_CONFIG.bottomText,
                    centerLine1: values.stampCenterLine1 || DEFAULT_STAMP_CONFIG.centerLine1,
                    centerLine2: values.stampCenterLine2 || DEFAULT_STAMP_CONFIG.centerLine2,
                    stampImageUrl: values.stampImageUrl,
                },
                textContent: {
                    referencePrefix: values.deductionReferencePrefix || DEFAULT_TEXT_CONTENT.referencePrefix,
                    formTitle: values.deductionFormTitle || DEFAULT_TEXT_CONTENT.formTitle,
                    subjectLine: values.deductionSubjectLine || DEFAULT_TEXT_CONTENT.subjectLine,
                    bodyText: values.deductionBodyText || DEFAULT_TEXT_CONTENT.bodyText,
                    consentTitle: values.deductionConsentTitle || DEFAULT_TEXT_CONTENT.consentTitle,
                    consentItems: consentItems.length > 0 ? consentItems : DEFAULT_TEXT_CONTENT.consentItems,
                    footerNote: values.deductionFooterNote || DEFAULT_TEXT_CONTENT.footerNote,
                    authorizationText: values.deductionAuthorizationText || DEFAULT_TEXT_CONTENT.authorizationText,
                    senderThankYouText: values.deductionSenderThankYouText || DEFAULT_TEXT_CONTENT.senderThankYouText,
                    signerTitle1: values.deductionSignerTitle1 || DEFAULT_TEXT_CONTENT.signerTitle1,
                    signerTitle2: values.deductionSignerTitle2 || DEFAULT_TEXT_CONTENT.signerTitle2,
                    receiverHeaderText: values.deductionReceiverHeaderText || DEFAULT_TEXT_CONTENT.receiverHeaderText,
                    tableSectionTitle: values.deductionTableSectionTitle || DEFAULT_TEXT_CONTENT.tableSectionTitle,
                    consentIntroText: values.deductionConsentIntroText || DEFAULT_TEXT_CONTENT.consentIntroText,
                    memberSignatureLabel: values.deductionMemberSignatureLabel || DEFAULT_TEXT_CONTENT.memberSignatureLabel,
                    dateLabelInConsent: values.deductionDateLabelInConsent || DEFAULT_TEXT_CONTENT.dateLabelInConsent,
                }
            } : last.deductionForm,
        };
        setConfigJson(JSON.stringify(config, null, 2));
    };

    const handleJsonChange = (value: string | undefined) => {
        if (!value) return;
        setConfigJson(value);
        try {
            const parsed = JSON.parse(value);
            lastParsedRef.current = parsed;
            setIsValidJson(true);
            parseAndSetFields(parsed);
        } catch (e) {
            setIsValidJson(false);
        }
    };

    const handleDeductionPreview = () => {
        try {
            const parsed = JSON.parse(configJson);
            const values = form.getFieldsValue();
            setPreviewConfig(buildDeductionFormConfig(parsed, values));
            setDeductionPreviewOpen(true);
        } catch {
            message.error('Cannot preview — invalid JSON configuration.');
        }
    };

    const handleApplicationPreview = () => {
        try {
            const parsed = JSON.parse(configJson);
            const values = form.getFieldsValue();
            const mergedConfig = {
                ...parsed,
                header: {
                    ...(parsed.header || {}),
                    companyName: values.companyName,
                    companyAddress: values.companyAddress,
                    companyPhone: values.companyPhone,
                    companyEmail: values.companyEmail,
                    formTitle: values.formTitle,
                    showLogo: values.showLogo ?? true,
                },
                branding: {
                    ...(parsed.branding || {}),
                    logoUrl: values.logoUrl,
                    watermarkUrl: values.watermarkUrl,
                    showWatermark: values.showWatermark ?? false,
                },
                applicationForm: {
                    display: {
                        approvalRows: Number(values.applicationApprovalRows || DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.approvalRows || 3),
                        showPenaltySection: values.applicationShowPenaltySection ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showPenaltySection ?? true,
                        showInsuranceSection: values.applicationShowInsuranceSection ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showInsuranceSection ?? true,
                        showInterestRate: values.applicationShowInterestRate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showInterestRate ?? true,
                        showDisbursementDate: values.applicationShowDisbursementDate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showDisbursementDate ?? true,
                        showMaturityDate: values.applicationShowMaturityDate ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showMaturityDate ?? true,
                        showTotalPrincipal: values.applicationShowTotalPrincipal ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalPrincipal ?? true,
                        showTotalInterest: values.applicationShowTotalInterest ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalInterest ?? true,
                        showTotalRepayment: values.applicationShowTotalRepayment ?? DEFAULT_LOAN_APPLICATION_FORM_CONFIG.applicationForm?.display?.showTotalRepayment ?? true,
                    },
                    textContent: buildApplicationTextContent(values),
                },
                deductionForm: parsed.deductionForm,
                displayOptions: {
                    ...(parsed.displayOptions || {}),
                    includeFeeBreakdown: values.includeFeeBreakdown ?? true,
                    includeRepaymentSchedule: values.includeRepaymentSchedule ?? false,
                    includeGuarantorInfo: values.includeGuarantorInfo ?? true,
                    includeSignatures: values.includeSignatures ?? true,
                },
            } as LoanApplicationTemplateConfig;

            setApplicationPreviewConfig(mergedConfig);
            setApplicationPreviewOpen(true);
        } catch {
            message.error('Cannot preview — invalid JSON configuration.');
        }
    };

    const handleSave = () => {
        if (!isValidJson) {
            message.error('Invalid JSON configuration. Please fix the errors.');
            return;
        }

        const values = form.getFieldsValue();
        onSave?.(configJson, values.logoUrl, values.watermarkUrl);
        message.success('Form configuration saved successfully');
    };

    const handleLoadHazinaDefault = () => {
        const hazinaConfig = stringifyLoanApplicationFormConfig(HAZINA_LOAN_APPLICATION_FORM_CONFIG);
        setConfigJson(hazinaConfig);
        try {
            const parsed = JSON.parse(hazinaConfig);
            parseAndSetFields(parsed);
            message.success('Hazina default form configuration loaded');
        } catch (e) {
            console.error('Error loading Hazina default config:', e);
            message.error('Failed to load Hazina default configuration');
        }
    };

    const handleReset = () => {
        const defaultConfig = getDefaultConfig();
        setConfigJson(defaultConfig);
        try {
            const parsed = JSON.parse(defaultConfig);
            parseAndSetFields(parsed, '/public/appearance/logo', '');
        } catch (e) {
            console.error('Error resetting config:', e);
        }
        message.info('Configuration reset to default');
    };

    return (
        <>
        <Card
            title={cardTitle}
            extra={
                <Space>
                    {showApplicationEditor && (
                        <Button icon={<EyeOutlined />} onClick={handleApplicationPreview}>
                            Preview Application Form
                        </Button>
                    )}
                    {showDeductionEditor && (
                        <Button icon={<EyeOutlined />} onClick={handleDeductionPreview}>
                            Preview Deduction Form
                        </Button>
                    )}
                    <Button onClick={handleLoadHazinaDefault}>
                        Load Hazina Default
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Reset to Default
                    </Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
                        Save Configuration
                    </Button>
                </Space>
            }
        >
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={<span><EyeOutlined /> Visual Editor</span>} key="visual">
                    <Form
                        form={form}
                        layout="vertical"
                        onValuesChange={handleVisualFormChange}
                    >
                        <Collapse defaultActiveKey={['basic', 'application-branding', 'application-text', 'deduction-branding', 'deduction-stamp', 'deduction-text', 'deduction-signature']}>
                            <Panel header="Basic Settings" key="basic">
                                <Form.Item label="Form Language" name="language" tooltip="Language for the forms">
                                    <Select placeholder="Select language">
                                        <Select.Option value="EN">English</Select.Option>
                                        <Select.Option value="SW">Swahili</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Company Name" name="companyName" tooltip="Company name to display in form header">
                                    <Input placeholder="e.g., HAZINA SACCOS" />
                                </Form.Item>
                                <Form.Item label="Company Address" name="companyAddress">
                                    <Input placeholder="e.g., Madaraka Avenue, P.O.BOX 9111" />
                                </Form.Item>
                                <Form.Item label="Company Phone" name="companyPhone">
                                    <Input placeholder="e.g., 0710 933944, 0710606605" />
                                </Form.Item>
                                <Form.Item label="Company Email" name="companyEmail">
                                    <Input placeholder="e.g., info@company.co.tz" />
                                </Form.Item>
                                {showApplicationEditor && (
                                    <Form.Item label="Application Form Title" name="formTitle">
                                        <Input placeholder="e.g., Loan Application Form" />
                                    </Form.Item>
                                )}
                                {showApplicationEditor && (
                                    <>
                                    <Form.Item label="Show Logo in Header" name="showLogo" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item label="Include Fee Breakdown" name="includeFeeBreakdown" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item label="Include Repayment Schedule" name="includeRepaymentSchedule" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item label="Include Guarantor Information" name="includeGuarantorInfo" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item label="Include Signatures" name="includeSignatures" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    </>
                                )}
                            </Panel>

                            {showApplicationEditor && (
                                <>
                            <Panel header="Application Form – Branding" key="application-branding">
                                <Form.Item
                                    label="Application Logo URL"
                                    name="logoUrl"
                                    tooltip="URL/path for the loan application form header logo. e.g. /images/hazina-logo.png"
                                >
                                    <Input placeholder="e.g. /images/hazina-logo.png" />
                                </Form.Item>
                                <Form.Item
                                    label="Application Watermark URL"
                                    name="watermarkUrl"
                                    tooltip="URL/path for watermark displayed behind the loan application form."
                                >
                                    <Input placeholder="e.g. /images/hazina-logo.png" />
                                </Form.Item>
                                <Form.Item label="Show Application Watermark" name="showWatermark" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Header Subtitle" name="applicationHeaderSubtitle">
                                    <Input placeholder="e.g. (Maendeleo, Biashara, Ujenzi, na Standing Order)" />
                                </Form.Item>
                                <Form.Item label="Reference Prefix" name="applicationReferencePrefix">
                                    <Input placeholder="e.g. HSCS" />
                                </Form.Item>
                                <Form.Item label="Reference Label" name="applicationReferenceLabel">
                                    <Input placeholder="e.g. Kumbukumbu" />
                                </Form.Item>
                                <Form.Item label="Date Label" name="applicationDateLabel">
                                    <Input placeholder="e.g. Tarehe" />
                                </Form.Item>
                            </Panel>

                            <Panel header="Application Form – Content" key="application-text">
                                <Form.Item label="Personal Section Title" name="applicationPersonalSectionTitle">
                                    <Input placeholder={applicationTextDefaults.personalSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Loan Section Title" name="applicationLoanSectionTitle">
                                    <Input placeholder={applicationTextDefaults.loanSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Monthly Installment Label" name="applicationMonthlyInstallmentLabel">
                                    <Input placeholder={applicationTextDefaults.monthlyInstallmentLabel} />
                                </Form.Item>
                                <Form.Item label="Interest Rate Label" name="applicationInterestRateLabel">
                                    <Input placeholder={applicationTextDefaults.interestRateLabel} />
                                </Form.Item>
                                <Form.Item label="Disbursement Date Label" name="applicationDisbursementDateLabel">
                                    <Input placeholder={applicationTextDefaults.disbursementDateLabel} />
                                </Form.Item>
                                <Form.Item label="Maturity Date Label" name="applicationMaturityDateLabel">
                                    <Input placeholder={applicationTextDefaults.maturityDateLabel} />
                                </Form.Item>
                                <Form.Item label="Total Principal Label" name="applicationTotalPrincipalLabel">
                                    <Input placeholder={applicationTextDefaults.totalPrincipalLabel} />
                                </Form.Item>
                                <Form.Item label="Total Interest Label" name="applicationTotalInterestLabel">
                                    <Input placeholder={applicationTextDefaults.totalInterestLabel} />
                                </Form.Item>
                                <Form.Item label="Total Repayment Label" name="applicationTotalRepaymentLabel">
                                    <Input placeholder={applicationTextDefaults.totalRepaymentLabel} />
                                </Form.Item>
                                <Form.Item label="Purpose Section Title" name="applicationPurposeSectionTitle">
                                    <Input placeholder={applicationTextDefaults.purposeSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Purpose Prompt" name="applicationPurposePrompt">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.purposePrompt} />
                                </Form.Item>
                                <Form.Item label="Collateral Section Title" name="applicationCollateralSectionTitle">
                                    <Input placeholder={applicationTextDefaults.collateralSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Collateral Summary" name="applicationCollateralSummary">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.collateralSummary} />
                                </Form.Item>
                                <Form.Item label="Collateral Description" name="applicationCollateralDescription">
                                    <TextArea rows={3} placeholder={applicationTextDefaults.collateralDescription} />
                                </Form.Item>
                                <Form.Item label="Collateral Commitment" name="applicationCollateralCommitment">
                                    <TextArea rows={3} placeholder={applicationTextDefaults.collateralCommitment} />
                                </Form.Item>
                                <Form.Item label="Penalty Section Title" name="applicationPenaltySectionTitle">
                                    <Input placeholder={applicationTextDefaults.penaltySectionTitle} />
                                </Form.Item>
                                <Form.Item label="Penalty Text" name="applicationPenaltyText">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.penaltyText} />
                                </Form.Item>
                                <Form.Item label="Account Section Title" name="applicationAccountSectionTitle">
                                    <Input placeholder={applicationTextDefaults.accountSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Account Prompt" name="applicationAccountPrompt">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.accountPrompt} />
                                </Form.Item>
                                <Form.Item label="Insurance / Charges Section Title" name="applicationInsuranceSectionTitle">
                                    <Input placeholder={applicationTextDefaults.insuranceSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Insurance / Charges Text" name="applicationInsuranceText">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.insuranceText} />
                                </Form.Item>
                                <Form.Item label="Declaration Title" name="applicationDeclarationTitle">
                                    <Input placeholder={applicationTextDefaults.applicantDeclarationTitle} />
                                </Form.Item>
                                <Form.Item label="Declaration Text" name="applicationDeclarationText">
                                    <TextArea rows={3} placeholder={applicationTextDefaults.applicantDeclarationText} />
                                </Form.Item>
                                <Form.Item label="Office Use Section Title" name="applicationOfficeUseSectionTitle">
                                    <Input placeholder={applicationTextDefaults.officeUseSectionTitle} />
                                </Form.Item>
                                <Form.Item label="Office Use Approval List Title" name="applicationOfficeUseApprovalListTitle">
                                    <Input placeholder={applicationTextDefaults.officeUseApprovalListTitle} />
                                </Form.Item>
                                <Form.Item label="Approval Rows" name="applicationApprovalRows">
                                    <Input placeholder="3" />
                                </Form.Item>
                                <Form.Item label="Footer Primary Text" name="applicationFooterPrimary">
                                    <Input placeholder={applicationTextDefaults.footerPrimary} />
                                </Form.Item>
                                <Form.Item label="Footer Secondary Text" name="applicationFooterSecondary">
                                    <TextArea rows={2} placeholder={applicationTextDefaults.footerSecondary} />
                                </Form.Item>
                            </Panel>

                            <Panel header="Application Form – Visibility" key="application-visibility">
                                <Form.Item label="Show Interest Rate" name="applicationShowInterestRate" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Disbursement Date" name="applicationShowDisbursementDate" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Maturity Date" name="applicationShowMaturityDate" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Total Principal" name="applicationShowTotalPrincipal" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Total Interest" name="applicationShowTotalInterest" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Total Repayment" name="applicationShowTotalRepayment" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Penalty Section" name="applicationShowPenaltySection" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                                <Form.Item label="Show Insurance Section" name="applicationShowInsuranceSection" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                            </Panel>
                                </>
                            )}

                            {showDeductionEditor && (
                                <>
                            <Panel header="Deduction Form – Branding (Logo & Stamp Image)" key="deduction-branding">
                                <Form.Item
                                    label="Logo URL"
                                    name="deductionLogoUrl"
                                    tooltip="URL/path for the logo shown on the deduction form header. e.g. /images/hazina-logo.png"
                                >
                                    <Input placeholder="e.g. /images/hazina-logo.png" />
                                </Form.Item>
                                <Form.Item
                                    label="Watermark URL"
                                    name="deductionWatermarkUrl"
                                    tooltip="URL/path for watermark displayed behind the deduction form. Leave empty to use logo as watermark."
                                >
                                    <Input placeholder="e.g. /images/hazina-logo.png (leave empty to use logo)" />
                                </Form.Item>
                                <Form.Item label="Show Watermark" name="deductionShowWatermark" valuePropName="checked" tooltip="Display watermark behind deduction form content">
                                    <Switch />
                                </Form.Item>
                                <Divider orientation="left" plain>Stamp Image (optional)</Divider>
                                <Form.Item
                                    label="Stamp Image URL"
                                    name="stampImageUrl"
                                    tooltip="URL/path to a stamp image file. When set, this image is displayed instead of the SVG drawn stamp. e.g. /images/hazina_stamp.svg"
                                >
                                    <Input placeholder="e.g. /images/hazina_stamp.svg" />
                                </Form.Item>
                            </Panel>

                            <Panel header="Deduction Form – Stamp (SVG)" key="deduction-stamp">
                                <Form.Item label="Stamp Shape" name="stampShape" tooltip="Shape of the official SVG stamp (used when Stamp Image URL is not set)">
                                    <Select placeholder="Select stamp shape" allowClear>
                                        <Select.Option value="circle">Circle (default)</Select.Option>
                                        <Select.Option value="rectangle">Rectangle</Select.Option>
                                        <Select.Option value="square">Square</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Stamp Color" name="stampColor" tooltip="Hex color for the stamp (e.g. #2E5CB8)">
                                    <Input placeholder="#2E5CB8" />
                                </Form.Item>
                                <Form.Item label="Organization Short Name" name="stampOrgName" tooltip="Short org name shown on stamp (e.g. HAZINA)">
                                    <Input placeholder="e.g. HAZINA" />
                                </Form.Item>
                                <Form.Item label="Top Arc Text" name="stampTopText">
                                    <Input placeholder="e.g. SAVINGS & CREDIT SOCIETY" />
                                </Form.Item>
                                <Form.Item label="Bottom Arc Text" name="stampBottomText">
                                    <Input placeholder="e.g. LIMITED" />
                                </Form.Item>
                                <Form.Item label="Center Line 1" name="stampCenterLine1" tooltip="First center line (e.g. P.O.Box address)">
                                    <Input placeholder="e.g. P.O.Box 9111" />
                                </Form.Item>
                                <Form.Item label="Center Line 2" name="stampCenterLine2" tooltip="Second center line (e.g. city)">
                                    <Input placeholder="e.g. DAR ES SALAAM" />
                                </Form.Item>
                            </Panel>

                            <Panel header="Deduction Form – Letter Text" key="deduction-text">
                                <Form.Item label="Reference Prefix" name="deductionReferencePrefix" tooltip="Prefix for the reference number (e.g. HSCS)">
                                    <Input placeholder="e.g. HSCS" />
                                </Form.Item>
                                <Form.Item label="Form Title (PDF)" name="deductionFormTitle" tooltip="Title at the top of the PDF deduction form">
                                    <Input placeholder="e.g. LOAN DEDUCTION FORM" />
                                </Form.Item>
                                <Form.Item label="Subject Line" name="deductionSubjectLine" tooltip="Subject line shown on the letter (YAH: ...)">
                                    <Input placeholder="e.g. KUWEKA MAKATO YA WANACHAMA" />
                                </Form.Item>
                                <Form.Item label="Body Text" name="deductionBodyText" tooltip="Descriptor inserted in the body paragraph">
                                    <Input placeholder="e.g. ambae anatakiwa kuwekewa makato ya Mkopo wa {productName}" />
                                </Form.Item>
                                <Form.Item label="Table Section Title" name="deductionTableSectionTitle" tooltip="Title above the loan table">
                                    <Input placeholder="e.g. LOANS ONN" />
                                </Form.Item>
                                <Form.Item label="Footer Note" name="deductionFooterNote" tooltip="Note at the bottom of the deduction form">
                                    <TextArea rows={3} placeholder="e.g. Fomu hii ni muhimu kwa ajili ya uthibitisho wa makato ya kila mwezi." />
                                </Form.Item>
                                <Form.Item label="Authorization Text (PDF)" name="deductionAuthorizationText" tooltip="Text used in the PDF deduction authorization section">
                                    <TextArea rows={2} placeholder="e.g. hereby authorize my employer to deduct the monthly installment from my salary" />
                                </Form.Item>
                            </Panel>

                            <Panel header="Deduction Form – Consent Section" key="deduction-consent">
                                <Form.Item label="Consent Section Title" name="deductionConsentTitle" tooltip="Title of the member consent box (shown underlined)">
                                    <Input placeholder="e.g. RIDHAA YA MWANACHAMA" />
                                </Form.Item>
                                <Form.Item label="Consent Intro Text" name="deductionConsentIntroText" tooltip="Text after member name in the consent box (e.g. ninakubali na kuthibitisha kwamba:)">
                                    <Input placeholder="e.g. ninakubali na kuthibitisha kwamba:" />
                                </Form.Item>
                                <Form.Item
                                    label="Consent Items (one per line)"
                                    name="deductionConsentItems"
                                    tooltip="Each line becomes one numbered consent item. Use {amount} for the installment amount and {companyName} for the company name."
                                >
                                    <TextArea
                                        rows={6}
                                        placeholder={DEFAULT_TEXT_CONTENT.consentItems.join('\n')}
                                    />
                                </Form.Item>
                                <Form.Item label="Member Signature Label" name="deductionMemberSignatureLabel">
                                    <Input placeholder="e.g. Sahihi ya Mwanachama:" />
                                </Form.Item>
                                <Form.Item label="Date Label in Consent" name="deductionDateLabelInConsent">
                                    <Input placeholder="e.g. Tarehe:" />
                                </Form.Item>
                            </Panel>

                            <Panel header="Deduction Form – Signature Section Text" key="deduction-signature">
                                <Form.Item label="Sender Thank-You Text" name="deductionSenderThankYouText" tooltip="Text above the officer signature line (left column)">
                                    <Input placeholder="e.g. Tunashukuru kwa ushirikiano wako," />
                                </Form.Item>
                                <Form.Item label="Signer Title 1" name="deductionSignerTitle1" tooltip="First title below the officer signature line">
                                    <Input placeholder="e.g. Ushirika Daima" />
                                </Form.Item>
                                <Form.Item label="Signer Title 2" name="deductionSignerTitle2" tooltip="Second title below the officer signature line (role)">
                                    <Input placeholder="e.g. DISBURSEMENT OFFICER" />
                                </Form.Item>
                                <Form.Item label="Receiver Header Text" name="deductionReceiverHeaderText" tooltip="Header text in the right column above the employer signature">
                                    <Input placeholder="e.g. Imepokelewa na kufanyiwa kazi na:" />
                                </Form.Item>
                            </Panel>
                                </>
                            )}
                        </Collapse>
                    </Form>
                </TabPane>

                <TabPane tab={<span><CodeOutlined /> JSON Editor</span>} key="json">
                    <div style={{ marginBottom: 16 }}>
                        {!isValidJson && (
                            <div style={{ color: 'red', marginBottom: 8 }}>
                                ⚠️ Invalid JSON. Please fix the syntax errors.
                            </div>
                        )}
                        <MonacoEditor
                            height="500px"
                            language="json"
                            theme={dark ? 'vs-dark' : 'light'}
                            value={configJson}
                            onChange={handleJsonChange}
                            options={{
                                minimap: { enabled: false },
                                formatOnPaste: true,
                                formatOnType: true,
                                automaticLayout: true,
                            }}
                        />
                    </div>
                </TabPane>
            </Tabs>
        </Card>

        {/* Deduction Form Preview Modal */}
        <Modal
            title="Deduction Form Preview"
            open={deductionPreviewOpen}
            onCancel={() => setDeductionPreviewOpen(false)}
            footer={<Button onClick={() => setDeductionPreviewOpen(false)}>Close</Button>}
            width={900}
            styles={{ body: { padding: '20px', maxHeight: '80vh', overflowY: 'auto' } }}
        >
            {previewConfig && (
                <WebDeductionFormComponent
                    deductionFormConfig={previewConfig}
                />
            )}
        </Modal>

        <Modal
            title="Application Form Preview"
            open={applicationPreviewOpen}
            onCancel={() => setApplicationPreviewOpen(false)}
            footer={<Button onClick={() => setApplicationPreviewOpen(false)}>Close</Button>}
            width={1000}
            styles={{ body: { height: '80vh', padding: 0 } }}
        >
            {applicationPreviewConfig && previewScheduleData ? (
                <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                    <SmartLoanApplicationDocument
                        user={authUser}
                        company={companyData}
                        product={previewProduct}
                        amount={previewAmount.toString()}
                        period={previewPeriod.toString()}
                        scheduleData={previewScheduleData}
                        formConfig={applicationPreviewConfig}
                    />
                </PDFViewer>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin spinning={isPreviewScheduleLoading} />
                </div>
            )}
        </Modal>
        </>
    );
};

export default FormTemplateConfigPanel;
